import axios from 'axios'

import Logger from '@/services/Logger'
import FirebaseInstance from '@/services/FirebaseInstance.js'

const logger = new Logger('AxiosInstance', false)
const AxiosInstance = axios.create()

const userToken = (refreshToken) =>
  new Promise((resolve) => {
    const unsub = FirebaseInstance.auth().onIdTokenChanged((user) => {
      unsub()
      if (user) {
        user
          .getIdToken(refreshToken)
          .then((token) => {
            resolve(token)
          })
          .catch((error) => {
            logger.error('error getting id token', error)
          })
      } else {
        logger.error('No firebase user available')
        resolve(false)
      }
    })
  })

/**
 *  Retry request and set _refreshed to refresh Firebase token.
 * */
const retryRequest = (request) => {
  request._refreshed = true
  logger.info('Retrying Request', request.url, request)
  return AxiosInstance.request(request)
}

/**
 * Intercept any Axios request and add the Firebase
 * Auth Token to its headers.
 */
AxiosInstance.interceptors.request.use(
  async (config) => {
    const refreshToken = config._refreshed || false
    let token = await userToken(refreshToken)

    if (!token) {
      logger.error('No token available, trying to login')

      try {
        await FirebaseInstance.auth().signInWithEmailAndPassword(
          _API_USER_LOGIN_,
          _API_USER_PASSWORD_
        )

        token = await userToken(refreshToken)
      } catch (error) {
        logger.error('Could not login to Firebase', error)
        return Promise.reject()
      }
    }

    if (!config.headers) {
      config.headers = {}
    }

    config.headers.token = token

    logger.info('Making Request', config.url, config)
    return config
  },
  (error) => {
    logger.error('Request Error', error)
    return Promise.reject(error)
  }
)
/**
 * Intercept any Axios response for Sentry logging
 */
AxiosInstance.interceptors.response.use(
  (success) => {
    logger.info('Response Received from', success.config.url, success)

    return success.data || success
  },
  (error) => {
    let errMessage = 'No error message available'
    logger.error('Axios error', error)

    if (error.response) {
      errMessage = error.response.data || error

      if (error.response.status === 403 && !error.config._refreshed && !error.response.data?.code) {
        return retryRequest(error.config)
      }
    } else if (error.message) {
      errMessage = error.message
    }

    return Promise.reject(errMessage)
  }
)

export default AxiosInstance
